import React, { useState, useEffect } from "react";
import { Box, TextField } from "@mui/material";

function OTPField({ length = 6, onChange }) {
    const [otp, setOtp] = useState(new Array(length).fill(""));
    const inputs = React.useRef([]);

    useEffect(() => {
        // Auto-focus on the first input when the component mounts
        inputs.current[0]?.focus();
    }, []);

    const handleChange = (value, index) => {
        if (!/^\d*$/.test(value)) return; // Allow only numbers

        const newOtp = [...otp];
        newOtp[index] = value;

        if (onChange) {
            onChange(newOtp.join(""));
        }

        // Move to the next input if it's filled
        if (value && index < length - 1) {
            inputs.current[index + 1]?.focus();
        }

        setOtp(newOtp);
    };

    const handleKeyDown = (e, index) => {
        if (e.key === "Backspace") {
            const newOtp = [...otp];
            if (!otp[index] && index > 0) {
                // Clear the previous input if the current one is empty
                inputs.current[index - 1]?.focus();
                newOtp[index - 1] = "";
            }
            newOtp[index] = "";
            setOtp(newOtp);

            if (onChange) {
                onChange(newOtp.join(""));
            }
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData("text").slice(0, length).replace(/\D/g, "");
        const newOtp = [...otp];
        for (let i = 0; i < pastedData.length; i++) {
            newOtp[i] = pastedData[i];
        }
        setOtp(newOtp);
        if (onChange) {
            onChange(newOtp.join(""));
        }
        inputs.current[Math.min(pastedData.length, length) - 1]?.focus();
    };

    const isPreviousFilled = (index) => {
        // Prevent typing unless the previous input is filled
        if (index === 0) return true; // Always allow typing in the first input
        return otp[index - 1] !== "";
    };

    return (
        <Box display="flex" gap={1} justifyContent="center" dir="ltr">
            {otp.map((digit, index) => (
                <TextField
                    key={index}
                    value={digit}
                    inputRef={(el) => (inputs.current[index] = el)}
                    onChange={(e) => {
                        if (isPreviousFilled(index)) {
                            handleChange(e.target.value, index);
                        }
                    }}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onPaste={handlePaste}
                    inputProps={{
                        maxLength: 1,
                        style: { textAlign: "center", fontSize: "1.5rem" },
                    }}
                    inputmode="numeric"
                    pattern="[0-9]*"
                    sx={{
                        width: "3rem",
                        height: "3rem", // Set equal height
                        "& .MuiInputBase-root": {
                            height: "3rem", // Ensure the input box itself matches height
                            borderRadius: "0.5rem",
                        },
                        "& .MuiOutlinedInput-input": {
                            textAlign: "center", // Center text
                            padding: "0.5rem",
                        },
                    }}
                />
            ))}
        </Box>
    );
}

export default OTPField;
